import { vorhandeneId } from './import';
import { supabase } from '../supabase/client';
import { Dienstleister, Kategorie } from './types';
import { ImportPlan } from './types';

export async function ladeKategorien(): Promise<Kategorie[]> {
  const { data } = await supabase.from('dienstleister_kategorien').select('*').order('sortierung');
  return (data as Kategorie[]) || [];
}

export async function setzeObjektLink(dienstleisterId: string, objektId: string, von: string | null, bis: string | null, vertragsart: string | null): Promise<void> {
  const { error } = await supabase.from('dienstleister_objekt').upsert({
    dienstleister_id: dienstleisterId, objekt_id: objektId, von, bis, vertragsart, aktiv: true,
  });
  if (error) throw new Error(error.message);
}

export async function entferneObjektLink(dienstleisterId: string, objektId: string): Promise<void> {
  const { error } = await supabase.from('dienstleister_objekt').delete()
    .eq('dienstleister_id', dienstleisterId).eq('objekt_id', objektId);
  if (error) throw new Error(error.message);
}

export async function ladeDienstleisterFuerObjekt(objektId: string): Promise<(Dienstleister & { kategorien: Kategorie[] })[]> {
  const { data: links } = await supabase.from('dienstleister_objekt').select('dienstleister_id').eq('objekt_id', objektId);
  const ids = (links || []).map((l: any) => l.dienstleister_id);
  if (ids.length === 0) return [];
  const [{ data: dls }, { data: kats }, { data: dkat }] = await Promise.all([
    supabase.from('dienstleister').select('*').in('id', ids),
    supabase.from('dienstleister_kategorien').select('*'),
    supabase.from('dienstleister_kategorie').select('dienstleister_id, kategorie_id').in('dienstleister_id', ids),
  ]);
  const katById = new Map((kats || []).map((k: any) => [k.id, k as Kategorie]));
  const katsOf = new Map<string, Kategorie[]>();
  (dkat || []).forEach((r: any) => {
    const arr = katsOf.get(r.dienstleister_id) || [];
    const k = katById.get(r.kategorie_id); if (k) arr.push(k);
    katsOf.set(r.dienstleister_id, arr);
  });
  return (dls || []).map((d: any) => ({ ...(d as Dienstleister), kategorien: katsOf.get(d.id) || [] }));
}

export async function schreibeImport(plan: ImportPlan): Promise<{ dienstleister: number; objektLinks: number; fehler: string[] }> {
  const kats = await ladeKategorien();
  const katByName = new Map(kats.map(k => [k.name.toLowerCase(), k.id]));
  let dlCount = 0, linkCount = 0;
  const fehler: string[] = [];
  for (const dl of plan.dienstleister) {
    try {
      // Upsert ueber Name + E-Mail (wie buildImportPlan und die Plan-Anzeige, ohne Gross-/Kleinschreibung)
      const { data: kandidaten, error: suchFehler } = await supabase.from('dienstleister').select('id, name, email')
        .ilike('name', dl.name.trim().replace(/[\\%_]/g, '\\$&'));
      if (suchFehler) throw new Error(suchFehler.message);
      let id = vorhandeneId(dl, (kandidaten ?? []) as { id: string; name: string; email: string | null }[]) ?? undefined;
      if (!id) {
        const { data, error } = await supabase.from('dienstleister').insert({
          name: dl.name, ansprechpartner: dl.ansprechpartner, email: dl.email, telefon: dl.telefon,
          strasse: dl.strasse, plz: dl.plz, stadt: dl.stadt,
        }).select('id').single();
        if (error || !data) throw new Error(error?.message ?? 'Dienstleister konnte nicht angelegt werden');
        id = (data as { id: string }).id; dlCount++;
      }
      // Kategorien (eine Abfrage je Dienstleister; unbekannte Namen werden übergangen)
      const katIds = [...new Set(dl.kategorien.map(k => katByName.get(k.toLowerCase())).filter((k): k is string => !!k))];
      if (katIds.length) {
        const { error: kf } = await supabase.from('dienstleister_kategorie').upsert(katIds.map(kid => ({ dienstleister_id: id, kategorie_id: kid })));
        if (kf) throw new Error(kf.message);
      }
      // Objekt-Links (eine Abfrage je Dienstleister)
      if (dl.objektLinks.length) {
        const { error: lf } = await supabase.from('dienstleister_objekt').upsert(dl.objektLinks.map(link => ({
          dienstleister_id: id, objekt_id: link.objekt_id, von: link.von, bis: link.bis, vertragsart: link.vertragsart, aktiv: true,
        })));
        if (lf) throw new Error(lf.message);
        linkCount += dl.objektLinks.length;
      }
    } catch (e) {
      fehler.push(`${dl.name}: ${e instanceof Error ? e.message : String(e)}`);
    }
  }
  return { dienstleister: dlCount, objektLinks: linkCount, fehler };
}
